import { motion } from "framer-motion";
import { Bot, Github, Twitter, MessageCircle, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";

const Footer = () => {
  const { t } = useI18n();

  return (
    <footer className="border-t border-border/50 py-16 px-6">
      <div className="container mx-auto max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="logo-frame w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold gradient-text">NATIBOT</span>
            </Link>
            <p className="text-sm text-muted-foreground">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                <Github className="w-5 h-5" />
              </a>
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">{t('footer.product')}</h4>
            <ul className="space-y-2">
              <FooterLink href="/features">{t('nav.features')}</FooterLink>
              <FooterLink href="/plugins">{t('nav.plugins')}</FooterLink>
              <FooterLink href="/premium">{t('nav.premium')}</FooterLink>
              <FooterLink href="/dashboard">{t('nav.dashboard')}</FooterLink>
            </ul>
          </div>

          {/* Resources */}
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">{t('footer.resources')}</h4>
            <ul className="space-y-2">
              <FooterLink href="/docs">{t('footer.documentation')}</FooterLink>
              <FooterLink href="/api">{t('footer.apiReference')}</FooterLink>
              <FooterLink href="/status">{t('footer.status')}</FooterLink>
              <FooterLink href="/changelog">{t('footer.changelog')}</FooterLink>
            </ul>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">{t('footer.support')}</h4>
            <ul className="space-y-2">
              <FooterLink href="/support/help-center">{t('footer.helpCenter')}</FooterLink>
              <FooterLink href="/support/discord">{t('footer.discordServer')}</FooterLink>
              <FooterLink href="/support/contact">{t('footer.contactUs')}</FooterLink>
              <FooterLink href="/support/report-bug">{t('footer.reportBug')}</FooterLink>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} NATIBOT. {t('footer.rights')}
          </p>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors">{t('footer.privacy')}</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">{t('footer.terms')}</Link>
            <Link to="/cookies" className="hover:text-foreground transition-colors">{t('footer.cookies')}</Link>
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            {t('footer.madeWith')} <Heart className="w-4 h-4 text-discord-red fill-discord-red" /> {t('footer.forDiscord')}
          </p>
        </div>
      </div>
    </footer>
  );
};

const FooterLink = ({ href, children }: { href: string; children: React.ReactNode }) => {
  const isExternal = href.startsWith('http');
  
  if (isExternal) {
    return (
      <li>
        <a 
          href={href} 
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {children}
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link 
        to={href} 
        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        {children}
      </Link>
    </li>
  );
};

export default Footer;
